import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from "react";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";
import {
  seedDemoFonceuse,
  seedDemoArchetypeEmployee,
  resetDemoEmployees as resetDemoEmployeesDb,
} from "@/services/demoSeedService";

export type Archetype = "fonceuse" | "procrastinateur" | "perle";
export type Genre = "homme" | "femme";
export type ScenarioId = "scenario_1" | "scenario_2" | "scenario_3";

export interface PersonaData {
  nom: string;
  genre: Genre;
}

export type PersonasMap = Record<Archetype, PersonaData>;

export const DEFAULT_PERSONAS: PersonasMap = {
  fonceuse: { nom: "Sophie", genre: "femme" },
  procrastinateur: { nom: "Marc", genre: "homme" },
  perle: { nom: "Camille", genre: "femme" },
};

const LS_KEY = "demo.v2.clientId";

interface DemoContextValue {
  demoClientId: string | null;
  demoClientName: string;
  personas: PersonasMap;
  currentScenario: ScenarioId | null;
  currentStep: number;
  totalSteps: number;
  isInDemo: boolean;
  demoEmployeeId: string | null;
  setDemoClient: (name: string) => Promise<void>;
  setPersona: (archetype: Archetype, nom: string, genre: Genre) => Promise<void>;
  startScenario: (id: ScenarioId, totalSteps: number) => Promise<void>;
  nextStep: () => Promise<void>;
  previousStep: () => Promise<void>;
  goToStep: (n: number) => Promise<void>;
  exitDemo: () => Promise<void>;
  resetDemoEmployees: () => Promise<void>;
}

const DemoContext = createContext<DemoContextValue | null>(null);

export function useDemo(): DemoContextValue {
  const ctx = useContext(DemoContext);
  if (!ctx) throw new Error("useDemo must be used within DemoProvider");
  return ctx;
}

export function DemoProvider({ children }: { children: ReactNode }) {
  const [demoClientId, setDemoClientId] = useState<string | null>(null);
  const [demoClientName, setDemoClientName] = useState<string>("");
  const [personas, setPersonas] = useState<PersonasMap>(DEFAULT_PERSONAS);
  const [currentScenario, setCurrentScenario] = useState<ScenarioId | null>(null);
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [totalSteps, setTotalSteps] = useState<number>(0);
  const [demoEmployeeId, setDemoEmployeeId] = useState<string | null>(null);

  const hydratedRef = useRef(false);

  // Hydrate from Supabase on mount if a clientId is in localStorage
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const id = typeof window !== "undefined" ? localStorage.getItem(LS_KEY) : null;
        if (!id) {
          hydratedRef.current = true;
          return;
        }
        const [{ data: client }, { data: personasRows }] =
          await Promise.all([
            supabase.from("demo_clients" as never).select("id, nom_entreprise").eq("id", id).maybeSingle(),
            supabase.from("demo_personas" as never).select("archetype, nom, genre").eq("demo_client_id", id),
          ]);

        if (cancelled) return;

        if (!client) {
          // Stale ref — clear it
          localStorage.removeItem(LS_KEY);
          hydratedRef.current = true;
          return;
        }

        setDemoClientId(id);
        setDemoClientName((client as { nom_entreprise: string }).nom_entreprise ?? "");

        if (personasRows && personasRows.length > 0) {
          const merged: PersonasMap = { ...DEFAULT_PERSONAS };
          for (const row of personasRows as Array<{ archetype: Archetype; nom: string | null; genre: Genre | null }>) {
            merged[row.archetype] = {
              nom: row.nom ?? DEFAULT_PERSONAS[row.archetype].nom,
              genre: row.genre ?? DEFAULT_PERSONAS[row.archetype].genre,
            };
          }
          setPersonas(merged);
        }
        // NOTE: We intentionally do NOT auto-restore an in-progress scenario.
        // Otherwise, a persisted demo progress row would re-trigger the
        // fullscreen overlay across the entire app on the next visit.
        // Users explicitly resume scenarios via the demo UI.
      } catch (err: unknown) {
        logger.error("DemoProvider hydrate failed", {
          stack: err instanceof Error ? err.stack : undefined,
        });
      } finally {
        hydratedRef.current = true;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const ensureClient = useCallback(
    async (nameFallback?: string): Promise<string> => {
      if (demoClientId) return demoClientId;
      const nom = nameFallback ?? demoClientName ?? "Client démo";
      const { data, error } = await supabase
        .from("demo_clients" as never)
        .insert({ nom_entreprise: nom })
        .select("id")
        .single();
      if (error || !data) throw error ?? new Error("Failed to create demo client");
      const id = (data as { id: string }).id;
      setDemoClientId(id);
      try {
        localStorage.setItem(LS_KEY, id);
      } catch {
        /* ignore */
      }
      return id;
    },
    [demoClientId, demoClientName],
  );

  const setDemoClient = useCallback(
    async (name: string) => {
      setDemoClientName(name);
      try {
        if (demoClientId) {
          await supabase
            .from("demo_clients" as never)
            .update({ nom_entreprise: name })
            .eq("id", demoClientId);
        } else {
          await ensureClient(name);
        }
      } catch (err: unknown) {
        logger.error("setDemoClient failed", { stack: err instanceof Error ? err.stack : undefined });
      }
    },
    [demoClientId, ensureClient],
  );

  const setPersona = useCallback(
    async (archetype: Archetype, nom: string, genre: Genre) => {
      setPersonas((prev) => ({ ...prev, [archetype]: { nom, genre } }));
      try {
        const id = await ensureClient();
        await supabase
          .from("demo_personas" as never)
          .upsert(
            { demo_client_id: id, archetype, nom, genre },
            { onConflict: "demo_client_id,archetype" },
          );
      } catch (err: unknown) {
        logger.error("setPersona failed", { stack: err instanceof Error ? err.stack : undefined });
      }
    },
    [ensureClient],
  );

  const persistProgress = useCallback(
    async (scenario: ScenarioId, step: number, completed: boolean) => {
      try {
        const id = await ensureClient();
        await supabase
          .from("demo_progress" as never)
          .upsert(
            {
              demo_client_id: id,
              scenario_id: scenario,
              current_step: step,
              completed,
              updated_at: new Date().toISOString(),
            },
            { onConflict: "demo_client_id,scenario_id" },
          );
      } catch (err: unknown) {
        logger.error("persistProgress failed", { stack: err instanceof Error ? err.stack : undefined });
      }
    },
    [ensureClient],
  );

  const startScenario = useCallback(
    async (id: ScenarioId, total: number) => {
      setCurrentScenario(id);
      setCurrentStep(0);
      setTotalSteps(total);
      await persistProgress(id, 0, false);
      // Provision the fictional employees (seedDemoFonceuse provisions all 3
      // archetypes — fonceuse, procrastinateur, perle — so any scenario is
      // ready to play). We trigger it for scenario_1 and scenario_3 since
      // those drive the in-app overlays on /rencontres-employes.
      if (id === "scenario_1" || id === "scenario_3") {
        try {
          const clientId = await ensureClient();
          const { data: { user } } = await supabase.auth.getUser();
          const empId =
            id === "scenario_1"
              ? await seedDemoFonceuse(clientId, personas, user?.id ?? null)
              : await seedDemoArchetypeEmployee("perle", clientId, personas, user?.id ?? null);
          setDemoEmployeeId(empId);
        } catch (err: unknown) {
          logger.error("seedDemoFonceuse failed", {
            stack: err instanceof Error ? err.stack : undefined,
          });
        }
      }
    },
    [persistProgress, ensureClient, personas],
  );

  const goToStep = useCallback(
    async (n: number) => {
      if (!currentScenario) return;
      const clamped = Math.max(0, totalSteps > 0 ? Math.min(n, totalSteps - 1) : n);
      setCurrentStep(clamped);
      await persistProgress(currentScenario, clamped, false);
    },
    [currentScenario, totalSteps, persistProgress],
  );

  const nextStep = useCallback(async () => {
    if (!currentScenario) return;
    await goToStep(currentStep + 1);
  }, [currentScenario, currentStep, goToStep]);

  const previousStep = useCallback(async () => {
    if (!currentScenario) return;
    await goToStep(currentStep - 1);
  }, [currentScenario, currentStep, goToStep]);

  const exitDemo = useCallback(async () => {
    if (currentScenario) {
      await persistProgress(currentScenario, currentStep, false);
    }
    setCurrentScenario(null);
    setCurrentStep(0);
    setTotalSteps(0);
    // Keep the demo employee on purpose so the prospect can resume the scenario later.
  }, [currentScenario, currentStep, persistProgress]);

  const resetDemoEmployees = useCallback(async () => {
    if (!demoClientId) return;
    try {
      await resetDemoEmployeesDb(demoClientId);
      setDemoEmployeeId(null);
    } catch (err: unknown) {
      logger.error("resetDemoEmployees failed", {
        stack: err instanceof Error ? err.stack : undefined,
      });
    }
  }, [demoClientId]);

  const value = useMemo<DemoContextValue>(
    () => ({
      demoClientId,
      demoClientName,
      personas,
      currentScenario,
      currentStep,
      totalSteps,
      isInDemo: currentScenario !== null,
      demoEmployeeId,
      setDemoClient,
      setPersona,
      startScenario,
      nextStep,
      previousStep,
      goToStep,
      exitDemo,
      resetDemoEmployees,
    }),
    [
      demoClientId,
      demoClientName,
      personas,
      currentScenario,
      currentStep,
      totalSteps,
      demoEmployeeId,
      setDemoClient,
      setPersona,
      startScenario,
      nextStep,
      previousStep,
      goToStep,
      exitDemo,
      resetDemoEmployees,
    ],
  );

  return <DemoContext.Provider value={value}>{children}</DemoContext.Provider>;
}